import { useMemo, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Landmark,
  ArrowRight,
  Search,
  FileText,
  ScrollText,
  Receipt,
  Info,
  CheckCircle2,
  XCircle,
  Percent,
  MinusCircle,
  RotateCcw,
  BookOpen,
  ExternalLink,
  ListChecks,
  AlertTriangle,
  Sparkles,
  PackageSearch,
  MapPinned,
} from "lucide-react";
import TanyaAI from "@/components/TanyaAI";
import BkpJkp from "@/components/BkpJkp";
import {
  ZONES,
  ZONE_LABELS,
  JENIS,
  JENIS_LABELS,
  PERATURAN,
  lookupPPN,
  classifyFasilitas,
} from "@/data/ppnData";

const STYLE = {
  collected: { badge: "bg-rose-100 text-rose-800 border-rose-200", icon: XCircle, label: "Dipungut PPN" },
  exempt: { badge: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Tidak Dipungut / Dibebaskan" },
  zero: { badge: "bg-sky-100 text-sky-800 border-sky-200", icon: Percent, label: "Tarif 0%" },
  outside: { badge: "bg-slate-100 text-slate-700 border-slate-200", icon: MinusCircle, label: "Luar Cakupan" },
  mixed: { badge: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Bervariasi — Perhatikan Ketentuan" },
  neutral: { badge: "bg-amber-100 text-amber-800 border-amber-200", icon: Info, label: "Informasi" },
};

function ZoneSelect({ id, label, value, onChange, options, labels, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </label>
      <div className="relative">
        <select
          id={id}
          data-testid={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm font-medium text-slate-800 shadow-sm outline-none transition-colors focus:border-[#0a3d62] focus:ring-2 focus:ring-[#0a3d62]/20 hover:border-slate-300"
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {labels[opt]}
            </option>
          ))}
        </select>
        <ArrowRight className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-slate-400" />
      </div>
    </div>
  );
}

function ResultRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 py-3.5">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0a3d62]/8 text-[#0a3d62]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="mt-0.5 whitespace-pre-line text-sm leading-relaxed text-slate-800">{children}</div>
      </div>
    </div>
  );
}

function NavTab({ id, active, onClick, icon: Icon, children }) {
  return (
    <button
      data-testid={id}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-1.5 py-2 text-[11px] font-semibold transition-colors sm:gap-2 sm:px-4 sm:text-sm ${
        active ? "bg-white text-[#0a3d62] shadow-sm" : "text-sky-100/90 hover:bg-white/10"
      }`}
    >
      <Icon className="hidden h-4 w-4 min-[400px]:block" />
      {children}
    </button>
  );
}

function Simulasi() {
  const [dari, setDari] = useState("");
  const [tujuan, setTujuan] = useState("");
  const [jenis, setJenis] = useState("");

  const result = useMemo(() => {
    if (!dari || !tujuan || !jenis) return undefined;
    return lookupPPN(dari, tujuan, jenis);
  }, [dari, tujuan, jenis]);

  const cls = result ? classifyFasilitas(result.fasilitas) : "neutral";
  const style = STYLE[cls] || STYLE.neutral;
  const StatusIcon = style.icon;

  const reset = () => {
    setDari("");
    setTujuan("");
    setJenis("");
  };

  return (
    <>
      {/* Form Card */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="form-card">
        <div className="mb-5 flex items-center gap-2">
          <Search className="h-5 w-5 text-[#0a3d62]" />
          <h2 className="text-lg font-bold text-slate-800">Pilih Skema Transaksi</h2>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <ZoneSelect id="select-dari" label="Dari (Asal)" value={dari} onChange={setDari} options={ZONES} labels={ZONE_LABELS} placeholder="Pilih asal…" />
          <ZoneSelect id="select-tujuan" label="Tujuan" value={tujuan} onChange={setTujuan} options={ZONES} labels={ZONE_LABELS} placeholder="Pilih tujuan…" />
          <ZoneSelect id="select-jenis" label="Jenis BKP / BKPTB / JKP" value={jenis} onChange={setJenis} options={JENIS} labels={JENIS_LABELS} placeholder="Pilih jenis…" />
        </div>

        {(dari || tujuan || jenis) && (
          <div className="mt-5 flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
              {dari && <span className="rounded-md bg-slate-100 px-2 py-1 font-medium">{dari}</span>}
              {dari && tujuan && <ArrowRight className="h-4 w-4 text-slate-400" />}
              {tujuan && <span className="rounded-md bg-slate-100 px-2 py-1 font-medium">{tujuan}</span>}
              {jenis && <span className="rounded-md bg-[#0a3d62]/8 px-2 py-1 font-medium text-[#0a3d62]">{jenis}</span>}
            </div>
            <button data-testid="reset-btn" onClick={reset} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
          </div>
        )}
      </section>

      {/* Result */}
      <AnimatePresence mode="wait">
        {result === undefined && (
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-14 text-center" data-testid="empty-state">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Receipt className="h-6 w-6 text-slate-400" />
            </div>
            <p className="mt-4 text-sm font-medium text-slate-500">Lengkapi ketiga pilihan di atas untuk melihat fasilitas PPN.</p>
          </motion.div>
        )}

        {result === null && (
          <motion.div key="notfound" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-6 py-10 text-center" data-testid="not-found-state">
            <Info className="mx-auto h-8 w-8 text-amber-500" />
            <p className="mt-3 text-sm font-semibold text-amber-800">Kombinasi ini tidak tersedia dalam tabel referensi.</p>
            <p className="mt-1 text-sm text-amber-700">Silakan pilih kombinasi Dari, Tujuan, dan Jenis yang lain.</p>
          </motion.div>
        )}

        {result && (
          <motion.section key={`${dari}-${tujuan}-${jenis}`} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="result-card">
            <div className="border-b border-slate-100 bg-gradient-to-r from-[#0a3d62] to-[#1e5f8c] px-6 py-6 text-white">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-sky-200">Fasilitas PPN</div>
                  <div className="mt-1 whitespace-pre-line text-xl font-bold leading-snug sm:text-2xl" data-testid="result-fasilitas">{result.fasilitas}</div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold ring-1 ring-white/25" data-testid="result-status-badge">
                  <StatusIcon className="h-4 w-4" />
                  {style.label}
                </span>
              </div>
            </div>

            <div className="divide-y divide-slate-100 px-6 py-2">
              <ResultRow icon={Receipt} label="Kode Faktur / Billing">{result.kode}</ResultRow>
              <ResultRow icon={FileText} label="Dokumen Kelengkapan">{result.dokumen}</ResultRow>
              <ResultRow icon={Info} label="Keterangan">{result.keterangan}</ResultRow>
              {result.dasar && (
                <ResultRow icon={ScrollText} label="Dasar Aturan"><span className="text-slate-600">{result.dasar}</span></ResultRow>
              )}
              {result.lanjut && (
                <ResultRow icon={ListChecks} label="Lebih Lanjut"><span className="text-slate-600">{result.lanjut}</span></ResultRow>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}

function Peraturan() {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="peraturan-section">
      <div className="mb-5 flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-[#0a3d62]" />
        <h2 className="text-lg font-bold text-slate-800">Dasar Peraturan</h2>
      </div>
      <p className="mb-6 text-sm text-slate-500">Kumpulan peraturan yang menjadi dasar fasilitas PPN. Klik untuk membuka dokumen resmi (JDIH Kementerian Keuangan).</p>

      <ul className="space-y-3">
        {PERATURAN.map((p, i) => (
          <li key={i}>
            <a
              data-testid={`peraturan-link-${i}`}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4 transition-colors hover:border-[#0a3d62]/40 hover:bg-[#0a3d62]/5"
            >
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0a3d62]/10 text-[#0a3d62]">
                <ScrollText className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-[#0a3d62]">{p.no}</div>
                <div className="mt-0.5 text-sm leading-relaxed text-slate-700">{p.judul}</div>
              </div>
              <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-slate-400 transition-colors group-hover:text-[#0a3d62]" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function App() {
  const [view, setView] = useState("kawasan");

  return (
    <div className="min-h-screen bg-[#f4f6fa] text-slate-900 [font-family:'Plus_Jakarta_Sans',sans-serif]">
      {/* Header */}
      <header className="relative overflow-hidden bg-[#0a3d62] text-white">
        <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]" />
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#1e5f8c]/40 blur-3xl" />
        <div className="relative mx-auto max-w-5xl px-6 py-10 sm:py-12">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 backdrop-blur">
              <Landmark className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="app-title">PPN-Check</h1>
              <p className="mt-1 text-sm text-sky-100/90 sm:text-base" data-testid="app-subtitle">
                Simulasi Fasilitas PPN antar Kawasan dan jenis BKP/JKP
              </p>
            </div>
          </div>

          {/* Nav */}
          <nav className="mt-6 grid grid-cols-4 gap-1 rounded-2xl bg-white/5 p-1 ring-1 ring-white/10 sm:flex sm:w-max sm:gap-2 sm:rounded-full" data-testid="main-nav">
            <NavTab id="nav-kawasan" active={view === "kawasan"} onClick={() => setView("kawasan")} icon={MapPinned}>Kawasan</NavTab>
            <NavTab id="nav-bkp-jkp" active={view === "bkp-jkp"} onClick={() => setView("bkp-jkp")} icon={PackageSearch}>BKP/JKP</NavTab>
            <NavTab id="nav-tanya-ai" active={view === "tanya-ai"} onClick={() => setView("tanya-ai")} icon={Sparkles}>Tanya AI</NavTab>
            <NavTab id="nav-peraturan" active={view === "peraturan"} onClick={() => setView("peraturan")} icon={BookOpen}>Peraturan</NavTab>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8 sm:py-10">
        {view === "kawasan" && <Simulasi />}
        {view === "bkp-jkp" && <BkpJkp />}
        {view === "tanya-ai" && <TanyaAI />}
        {view === "peraturan" && <Peraturan />}

        {/* Disclaimer */}
        {view !== "tanya-ai" && (
        <div className="mt-6 flex gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-5 py-4" data-testid="disclaimer">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm leading-relaxed text-slate-700">
            <p className="font-bold text-amber-800">Perhatikan jenis BKP tertentu dan JKP tertentu!</p>
            <p className="mt-1 text-slate-600">
              Aplikasi ini sebagai alat bantu untuk melihat fasilitas PPN, jika terdapat kekeliruan atau kesalahan
              penerapan aturan, silakan berkonsultasi kepada instansi terkait.
            </p>
          </div>
        </div>
        )}
      </main>

      {/* Footer credit */}
      <footer className="border-t border-slate-200 bg-white py-6">
        <div className="mx-auto max-w-5xl px-6 text-center text-sm text-slate-500">
          Dibuat oleh: <span className="font-semibold text-[#0a3d62]">Penyuluh KPP Pratama Batam Utara — 2026</span>
        </div>
      </footer>
    </div>
  );
}
